import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { TaxedDisposal, sumAud } from '../../utils/cgtCalculator';
import { CoinIcon } from '../Transactions/CoinIcon';
import { formatAUD, formatAmount, formatDate, formatDateShort, formatMarketPrice } from '../../utils/formatters';
import { InfoTooltip } from '../ui/InfoTooltip';

const GREEN = '#6db981';
const RED = '#c4615e';
const BRONZE = '#b89968';

// Shared desktop (sm+) column template so every collapsed row lines up, and the
// expanded detail grid can align its columns to the same left edges. Six tracks:
// identity · Amount · Sale price · Gain · CGT · badge/chevron. The four middle
// tracks are equal (1fr) — the expanded grid reuses this template and drops its
// metrics into those same middle tracks (columns 2–5) so they align exactly.
const SM_GRID = 'minmax(0,1.7fr) repeat(5, minmax(0,1fr))';

// One realised SELL, with its FIFO sub-disposals (parcels) grouped together.
export interface DisposalGroup {
  sellId: string;
  coinId: string;
  symbol: string;
  name: string;
  disposalDate: string;
  disposalTs: number;
  totalQuantity: number;
  totalProceeds: number;   // AUD, summed from the parcels' own per-leg conversions
  totalCostBasis: number;  // AUD
  totalGain: number;       // AUD
  wallet?: string;
  parcels: TaxedDisposal[];
  /** True when any parcel fell back to the live spot rate on either leg. */
  fxEstimated: boolean;
}

function Metric({ label, value, color, className }: { label: string; value: React.ReactNode; color?: string; className?: string }) {
  return (
    <div className={`min-w-0 ${className ?? ''}`}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</p>
      <p
        className={`text-sm font-medium tabular-nums truncate ${color ?? 'text-slate-100'}`}
        title={typeof value === 'string' ? value : undefined}
      >
        {value}
      </p>
    </div>
  );
}

const months = (days: number) => {
  const m = Math.round(days / 30.436875);
  return m < 1 ? '<1mo' : `${m} month${m === 1 ? '' : 's'}`;
};

/** Human name for the feed a leg's rate came from. */
const SOURCE_NAMES: Record<string, string> = {
  rba: 'RBA daily',
  ecb: 'ECB daily',
  manual: 'Entered manually',
  carried: 'Last known rate',
  spot: 'Today’s rate',
};

/** The distinct rate sources across a group's legs, as a short label. */
function fxSourceLabel(g: DisposalGroup): string {
  const sources = new Set<string>();
  for (const p of g.parcels) {
    sources.add(p.acquisitionFx.source);
    sources.add(p.disposalFx.source);
  }
  return Array.from(sources)
    .map((s) => SOURCE_NAMES[s] ?? s)
    .join(' + ');
}

function fxTooltip(g: DisposalGroup): string {
  const base =
    'Each leg is converted to AUD at the exchange rate for its own date. The acquisition uses the buy date’s rate, the disposal the sell date’s. A weekend or public holiday uses the previous business day.';
  return g.fxEstimated
    ? `${base} No published rate could be found for at least one of these dates, so the most recent earlier rate was used (or today’s, if there was none) and this figure is approximate.`
    : base;
}

function parcelBadge(p: TaxedDisposal): { label: string; color: string; bg: string } {
  if (p.gain < 0) return { label: 'loss', color: RED, bg: 'rgba(196,97,94,0.12)' };
  if (p.discountEligible) return { label: '50% discount', color: GREEN, bg: 'rgba(109,185,129,0.12)' };
  return { label: 'no discount', color: 'var(--color-sub)', bg: 'rgb(var(--overlay) / 0.05)' };
}

interface Props {
  group: DisposalGroup;
  marginalRate: number;
  bracketLabel: string;
  imageMap: Record<string, string>;
}

export function RealisedDisposalRow({ group: g, marginalRate, bracketLabel, imageMap }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  // Every figure below is AUD and was rounded ONCE, in the engine. This component
  // only adds up the parcels the engine already produced — it must not re-derive
  // them (no × 0.5, no × marginalRate here), because that is exactly how the
  // screen and the exports drift a cent apart.
  const discountAmount = sumAud(g.parcels.map((p) => p.discountAmount));
  const taxableGain    = sumAud(g.parcels.map((p) => p.taxableGain));
  const cgtOwed        = sumAud(g.parcels.map((p) => p.cgtOwed));
  const sellPrice      = g.totalQuantity > 0 ? g.totalProceeds / g.totalQuantity : 0;
  const costPerCoin    = g.totalQuantity > 0 ? g.totalCostBasis / g.totalQuantity : 0;
  const isLoss         = g.totalGain < 0;
  const anyDiscount    = discountAmount > 0;
  const multiParcel    = g.parcels.length > 1;

  const badge = isLoss
    ? { label: 'loss', color: RED, bg: 'rgba(196,97,94,0.12)' }
    : anyDiscount
      ? { label: '50% discount', color: GREEN, bg: 'rgba(109,185,129,0.12)' }
      : { label: 'no discount', color: 'var(--color-sub)', bg: 'rgb(var(--overlay) / 0.05)' };

  const ratePct = `${(marginalRate * 100).toFixed(marginalRate * 100 % 1 === 0 ? 0 : 1)}%`;

  const goToCoin = (ev: React.MouseEvent | React.KeyboardEvent) => {
    ev.stopPropagation();
    navigate(`/coin/${g.coinId}`, { state: { from: 'tax' } });
  };

  return (
    <div className="rounded-xl border border-slate-700/60 transition-colors hover:bg-[rgb(var(--overlay)/0.03)]">
      {/* Summary row — clicking anywhere (except the coin name) expands/collapses */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setOpen((o) => !o); } }}
        aria-expanded={open}
        className="w-full flex sm:grid items-center gap-4 p-4 text-left cursor-pointer"
        style={{ gridTemplateColumns: SM_GRID }}
      >
        {/* Identity (col 1) — only the coin NAME navigates */}
        <div className="flex items-center gap-3 min-w-0 w-[200px] sm:w-auto flex-shrink-0">
          <CoinIcon coinId={g.coinId} symbol={g.symbol} imageMap={imageMap} size={28} />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">
              <span
                role="button"
                tabIndex={0}
                onClick={goToCoin}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); goToCoin(ev); } }}
                title={`View ${g.name}`}
                className="text-slate-100 hover:text-[#d4b483] cursor-pointer transition-colors"
              >
                {g.name}
              </span>{' '}
              <span className="text-slate-500 font-normal">{g.symbol}</span>
            </p>
            <p className="text-xs truncate" style={{ color: BRONZE }}>Sold {formatDateShort(g.disposalDate)}</p>
          </div>
        </div>

        {/* Middle — key numbers (cols 2–5; hidden on mobile, shown in the expanded view) */}
        <Metric className="hidden sm:block" label="Amount" value={`${formatAmount(g.totalQuantity)} ${g.symbol}`} />
        <Metric className="hidden sm:block" label="Sale price" value={formatMarketPrice(sellPrice, 'AUD')} />
        <Metric className="hidden sm:block" label="Gain" value={formatAUD(g.totalGain, { sign: true })} color={g.totalGain >= 0 ? 'text-emerald-400' : 'text-red-400'} />
        <Metric className="hidden sm:block" label="Taxable gain" value={formatAUD(taxableGain)} />

        {/* Right — badge + chevron (col 6) */}
        <div className="flex items-center gap-3 flex-shrink-0 ml-auto sm:ml-0 sm:justify-end">
          <span
            className="text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap"
            style={{ background: badge.bg, color: badge.color, border: `0.5px solid ${badge.color}33` }}
          >
            {badge.label}
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Expanded breakdown */}
      <div
        className="grid"
        style={{ gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows 220ms ease-in-out' }}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="px-4 pb-4 pt-1 border-t border-slate-700/40">
            {(() => {
              const detailMetrics = [
                <Metric key="ddate" label="Disposal date" value={formatDate(g.disposalDate)} />,
                ...(!multiParcel ? [
                  <Metric key="adate" label="Acquisition date" value={formatDate(g.parcels[0].acquisitionDate)} />,
                  <Metric key="hold" label="Hold duration" value={months(g.parcels[0].heldDays)} />,
                ] : []),
                <Metric key="amt" label="Amount sold" value={`${formatAmount(g.totalQuantity)} ${g.symbol}`} />,
                <Metric key="cpc" label="Cost basis (per coin)" value={formatMarketPrice(costPerCoin, 'AUD')} />,
                <Metric key="tcb" label="Total cost basis" value={formatAUD(g.totalCostBasis)} />,
                <Metric key="tp" label="Total proceeds" value={formatAUD(g.totalProceeds)} />,
                <Metric key="gl" label="Capital gain/loss" value={formatAUD(g.totalGain, { sign: true })} color={g.totalGain >= 0 ? 'text-emerald-400' : 'text-red-400'} />,
                <Metric key="disc" label="CGT discount" value={anyDiscount ? '50% applied' : 'Not eligible'} color={anyDiscount ? 'text-emerald-400' : 'text-slate-400'} />,
                <Metric key="discamt" label="Discount amount" value={discountAmount > 0 ? `−${formatAUD(discountAmount)}` : formatAUD(0)} />,
                <Metric key="brk" label="Tax bracket" value={bracketLabel} />,
                <Metric key="rate" label="Marginal rate" value={ratePct} />,
                <div key="cgt" className="min-w-0">
                  <div className="flex items-center gap-1 mb-0.5">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">CGT on this gain</p>
                    <InfoTooltip text="Calculated on this disposal alone, before capital losses elsewhere in this financial year are offset. The payable figure is the Est. CGT owed in the summary at the top." />
                  </div>
                  <p className="text-sm font-medium tabular-nums truncate text-slate-100" title={formatAUD(cgtOwed)}>
                    {formatAUD(cgtOwed)}
                  </p>
                </div>,
                <Metric key="wallet" label="Wallet" value={g.wallet || '—'} />,
                // Provenance for the AUD figures above. A tax report should be
                // able to say where its exchange rate came from, and a row whose
                // rate could not be resolved should not look like one that could.
                <div key="fx" className="min-w-0">
                  <div className="flex items-center gap-1 mb-0.5">
                    <p className="text-[10px] uppercase tracking-wider text-slate-500">FX basis</p>
                    <InfoTooltip text={fxTooltip(g)} />
                  </div>
                  <p
                    className={`text-sm font-medium truncate ${g.fxEstimated ? 'text-amber-400' : 'text-slate-100'}`}
                  >
                    {g.fxEstimated ? 'Approximate' : fxSourceLabel(g)}
                  </p>
                </div>,
              ];
              return (
                <>
                  {/* Mobile — simple readable 2-col grid */}
                  <div className="sm:hidden grid grid-cols-2 gap-4 mt-3">{detailMetrics}</div>
                  {/* Desktop — reuse the summary row's template; the metrics live in
                      the same middle tracks (columns 2–5), so their left edges line
                      up with the collapsed Amount / Sale price / Gain / CGT columns. */}
                  <div className="hidden sm:grid gap-4 mt-3" style={{ gridTemplateColumns: SM_GRID }}>
                    <div className="grid grid-cols-4 gap-4" style={{ gridColumn: '2 / 6' }}>{detailMetrics}</div>
                  </div>
                </>
              );
            })()}

            {/* Multi-parcel breakdown (FIFO) */}
            {multiParcel && (
              <div className="mt-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">FIFO parcels ({g.parcels.length})</p>
                  <InfoTooltip text="First in, first out. When you sell part of a holding, the oldest parcel is treated as sold first. MyCryptoPlanner uses FIFO for all estimates." />
                </div>
                <div className="flex flex-col gap-1">
                  {g.parcels.map((p, i) => {
                    const pb = parcelBadge(p);
                    const pCost = p.quantity > 0 ? p.costBasis / p.quantity : 0;
                    return (
                      <div
                        key={`${p.acquisitionDate}-${i}`}
                        className="flex items-center gap-3 rounded-lg px-2.5 py-1.5"
                        style={{ background: 'rgb(var(--overlay) / 0.02)', border: '0.5px solid rgb(var(--overlay) / 0.05)' }}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-[12px] text-slate-200 truncate">
                            {formatAmount(p.quantity)} {g.symbol} · acquired {formatDateShort(p.acquisitionDate)}
                          </p>
                          <p className="text-[11px] truncate" style={{ color: 'var(--color-sub)' }}>
                            held {months(p.heldDays)} · cost {formatMarketPrice(pCost, 'AUD')}/coin
                          </p>
                        </div>
                        <span
                          className="text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap flex-shrink-0"
                          style={{ background: pb.bg, color: pb.color, border: `0.5px solid ${pb.color}33` }}
                        >
                          {pb.label}
                        </span>
                        <span
                          className="num text-[12px] font-medium flex-shrink-0 text-right min-w-[80px]"
                          style={{ color: p.gain >= 0 ? GREEN : RED }}
                        >
                          {p.gain >= 0 ? '+' : '−'}{formatAUD(Math.abs(p.gain))}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
